/**
 * Shared per-symptom create settlement (#2236).
 *
 * Both symptom writers (useLogSymptoms, useCreateSymptoms) fan a form out
 * into one POST per symptom row. Settling them through the receipt ledger
 * keeps a partial failure retryable without duplicating the rows the server
 * already accepted. Symptom rows are content-addressed: the key IS the
 * signature, so an edited row is a new logical row (symptoms are append-only
 * creates — there is nothing to update in place).
 *
 * Exported as a pure function so the production batch semantics are
 * testable with a deterministic `post` (#2236 review).
 */

import {
  type BatchNoWrite,
  type BatchOutcome,
  type BatchRetryLedger,
  settleBatchWithLedgerOrThrow,
} from "./batch-create";

export type SymptomCreateBody = {
  symptom: string;
  dateAndTime: string;
  notes?: string | null;
};

export type SymptomCreateItem = {
  /**
   * Stable content key. Must not include volatile parts (e.g. a re-computed
   * "now" timestamp) or a retry would re-create already-accepted rows.
   */
  key: string;
  body: SymptomCreateBody;
};

/**
 * Build stable keys for a symptom batch. The occurrence suffix keeps two
 * identical symptom rows distinct while remaining stable if the rows are
 * reordered before a retry.
 */
export function buildSymptomCreateItems(
  keyPrefix: string,
  bodies: readonly SymptomCreateBody[],
): SymptomCreateItem[] {
  const occurrences = new Map<string, number>();

  return bodies.map((body) => {
    const baseKey = `${keyPrefix}:${body.symptom}:${body.notes ?? ""}`;
    const occurrence = occurrences.get(baseKey) ?? 0;
    occurrences.set(baseKey, occurrence + 1);

    return {
      key: `${baseKey}:${occurrence}`,
      body,
    };
  });
}

export async function settleSymptomCreateBatch<TResult>(
  items: readonly SymptomCreateItem[],
  post: (body: SymptomCreateBody) => Promise<TResult>,
  ledger: BatchRetryLedger<TResult>,
  describe: (
    outcome: BatchOutcome<SymptomCreateItem, TResult | BatchNoWrite>,
  ) => string,
): Promise<TResult[]> {
  return settleBatchWithLedgerOrThrow(
    items,
    (item) => ({ key: item.key, signature: item.key }),
    (item) => post(item.body),
    ledger,
    describe,
  );
}
